import React, { useState } from 'react';
import {
  Avatar,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  VolunteerActivism as DonorIcon,
  PersonSearch as RecipientIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import PageContainer from '../components/PageContainer';
import { API_URL } from '../config';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errorMsg, setErrorMsg] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setErrorMsg(null);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Core sign-in routine, reused by the form submit and the demo buttons.
  const performLogin = async (email, password) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/signin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.error) {
        setErrorMsg(data.error);
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: data.error,
          confirmButtonColor: theme.palette.primary.main,
        });
      } else {
        Swal.fire({
          title: "Success",
          text: "Login Successful",
          icon: "success",
          confirmButtonColor: theme.palette.primary.main,
        });

        localStorage.setItem('clientToken', JSON.stringify(data));

        if (data?.data === 'admin') {
          navigate("/admin");
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMsg('Server error. Please try again later.');
      Swal.fire({
        icon: "error",
        title: "Server Error",
        text: "Could not connect to the server. Please try again later.",
        confirmButtonColor: theme.palette.primary.main,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    // Input validation
    if (!formData.email || !formData.password) {
      setErrorMsg('All fields are required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMsg('Please enter a valid email address');
      return;
    }

    performLogin(formData.email, formData.password);
  };

  // Pre-seeded demo accounts so reviewers can explore each role with one click.
  const DEMO_PASSWORD = 'Demo@12345';
  const demoAccounts = [
    { role: 'Admin', email: 'admin@careshare.app', icon: <AdminIcon />, color: '#264653' },
    { role: 'Donor', email: 'donor@careshare.app', icon: <DonorIcon />, color: '#2A9D8F' },
    { role: 'Recipient', email: 'recipient@careshare.app', icon: <RecipientIcon />, color: '#E76F51' },
  ];

  const handleDemoLogin = (email) => {
    setFormData({ email, password: DEMO_PASSWORD });
    setErrorMsg(null);
    performLogin(email, DEMO_PASSWORD);
  };

  return (
    <PageContainer sx={{ display: 'flex', alignItems: 'center' }}>
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 4,
              boxShadow: '0 15px 50px rgba(0,0,0,0.1)',
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(250, 252, 252, 1) 100%)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '6px',
                background: 'linear-gradient(90deg, #2A9D8F 0%, #83C5BE 100%)',
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Avatar 
                sx={{ 
                  mb: 2, 
                  width: 70, 
                  height: 70, 
                  bgcolor: theme.palette.primary.main,
                  boxShadow: '0 8px 20px rgba(42, 157, 143, 0.25)',
                }}
              >
                <LoginIcon sx={{ fontSize: 36 }} />
              </Avatar>
            </motion.div>
            
            <Typography 
              component="h1" 
              variant="h4" 
              fontWeight="bold" 
              gutterBottom
              sx={{ 
                mb: 0.5,
                color: theme.palette.text.primary,
                textAlign: 'center'
              }}
            >
              Welcome Back
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              align="center" 
              sx={{ 
                mb: 3,
                maxWidth: '85%'
              }}
            >
              Sign in to continue to CareShare and make a difference
            </Typography>
            
            {errorMsg && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Alert 
                  severity="error" 
                  sx={{ 
                    width: '100%', 
                    mb: 2,
                    borderRadius: 2
                  }}
                >
                  {errorMsg}
                </Alert>
              </motion.div>
            )}
            
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <motion.div
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={formData.email}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
              </motion.div>
              
              <motion.div
                initial={{ x: -10, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
              </motion.div>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      value="remember" 
                      color="primary" 
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me"
                />
              </Box>
              
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: theme.shape.borderRadius * 3,
                    position: 'relative',
                    boxShadow: '0 8px 20px rgba(42, 157, 143, 0.2)',
                    '&:hover': {
                      boxShadow: '0 10px 25px rgba(42, 157, 143, 0.3)',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </motion.div>
              
              <Box sx={{ width: '100%', mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    For reviewers — one-click demo login
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {demoAccounts.map((acc) => (
                    <Button
                      key={acc.role}
                      onClick={() => handleDemoLogin(acc.email)}
                      disabled={isLoading}
                      variant="outlined"
                      fullWidth
                      startIcon={acc.icon}
                      sx={{
                        flexDirection: 'column',
                        py: 1,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        color: acc.color,
                        borderColor: alpha(acc.color, 0.4),
                        '& .MuiButton-startIcon': { m: 0, mb: 0.5 },
                        '&:hover': {
                          borderColor: acc.color,
                          bgcolor: alpha(acc.color, 0.06),
                        },
                      }}
                    >
                      {acc.role}
                    </Button>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                  Password for all demo accounts: <b>{DEMO_PASSWORD}</b>
                </Typography>
              </Box>

              <Grid container justifyContent="center" sx={{ mt: 3 }}>
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Don't have an account?{' '}
                    <Link 
                      component={RouterLink} 
                      to="/signup" 
                      sx={{ 
                        fontWeight: 600, 
                        color: theme.palette.primary.main,
                        transition: 'all 0.2s',
                        position: 'relative',
                        '&:hover': {
                          color: theme.palette.primary.dark,
                          textDecoration: 'none',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          width: '0%',
                          height: '2px',
                          bottom: -1,
                          left: 0,
                          backgroundColor: theme.palette.primary.main,
                          transition: 'width 0.3s ease',
                        },
                        '&:hover::after': {
                          width: '100%',
                        }
                      }}
                    >
                      Sign Up
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </PageContainer>
  );
};

export default Login;